// Individual groups use native jump links. Compact selectors swap only their
// own articles, preserving Horizon's deferred models and commerce ownership.
import { getScrollContainer } from '@theme/scroll-container';

class SterlingBundleNavigation extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.selector = this.dataset.layout === 'selector';
    this.nav = this.querySelector(this.selector ? '.sterling-bundle-showcase__index' : '.sterling-bundle-showcase__navigation');
    this.links = [...this.nav.querySelectorAll('a')];
    this.targets = this.links.map((link) => document.getElementById(link.hash.slice(1)));
    if (this.selector) {
      this.links.unshift(null);
      this.targets.unshift(this.querySelector('.sterling-bundle-showcase__header'));
    }
    this.frame = null;
    this.schedule = () => {
      if (this.frame !== null) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    };
    if (this.selector) {
      this.dataset.selectorReady = 'true';
      this.paging = this.querySelector('.sterling-bundle-showcase__paging');
      this.paging.hidden = false;
      this.selected = 1;
      this.addEventListener('click', (event) => {
        const link = event.target.closest('a[href^="#"]');
        const index = this.targets.findIndex((target) => target?.id === link?.hash.slice(1));
        if (index > 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
          event.preventDefault();
          this.choose(index);
        }
        const step = event.target.closest('button[data-step]');
        if (step) this.choose(this.selected + Number(step.dataset.step));
      }, { signal });
      this.selectFromHash = () => {
        const index = this.targets.findIndex((target) => `#${target?.id}` === location.hash);
        if (index > 0) {
          this.select(index);
        }
      };
      window.addEventListener('hashchange', this.selectFromHash, { signal });
      this.select(this.targets.findIndex((target) => `#${target?.id}` === location.hash) > 0 ? this.targets.findIndex((target) => `#${target?.id}` === location.hash) : 1);
    }
    // Capture observes document and .page-wrapper scrolling across breakpoints.
    document.addEventListener('scroll', this.schedule, { capture: true, passive: true, signal });
    window.addEventListener('resize', this.schedule, { signal });
    window.addEventListener('hashchange', this.schedule, { signal });
    this.observer = new ResizeObserver(this.schedule);
    this.observer.observe(this.nav);
    this.header = document.querySelector('#header-component');
    if (this.header) this.observer.observe(this.header);
    this.schedule();
  }

  choose(index) {
    if (index < 1 || index >= this.targets.length || index === this.selected) return;
    const hash = `#${this.targets[index].id}`;
    if (location.hash !== hash) history.pushState({ ...history.state, scrollTop: getScrollContainer().scrollTop }, '', hash);
    this.select(index);
  }

  select(index) {
    this.selected = index;
    this.targets.slice(1).forEach((target, offset) => {
      const hidden = offset + 1 !== index;
      if (hidden) target.querySelector('product-model')?.pauseMedia?.();
      target.hidden = hidden;
    });
    this.paging.querySelector('[data-step="-1"]').disabled = index === 1;
    this.paging.querySelector('[data-step="1"]').disabled = index === this.targets.length - 1;
    this.paging.querySelector('output').textContent = `${index} of ${this.targets.length - 1}`;
    this.schedule?.();
  }

  update() {
    const headerRect = this.header?.getBoundingClientRect();
    const headerBottom = headerRect && headerRect.top < innerHeight && headerRect.bottom > 0 ? headerRect.bottom : 0;
    const top = Math.max(0, headerBottom) + 8;
    const height = this.selector ? 0 : this.nav.getBoundingClientRect().height;
    this.style.setProperty('--bundle-nav-top', `${top}px`);
    this.style.setProperty('--bundle-nav-height', `${height}px`);
    const boundary = top + height + 24;
    let active = 0;
    if (this.selector) {
      active = this.selected;
    } else {
      this.targets.forEach((target, index) => {
        if (target && target.getBoundingClientRect().top <= boundary) active = index;
      });
    }
    if (active === this.active) return;
    this.active = active;
    this.links.forEach((link, index) => {
      if (!link) return;
      if (index === active) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
    if (this.selector) return;
    const link = this.links[active];
    const navRect = this.nav.getBoundingClientRect();
    const linkRect = link.getBoundingClientRect();
    if (linkRect.left < navRect.left + 8) this.nav.scrollLeft -= navRect.left + 8 - linkRect.left;
    else if (linkRect.right > navRect.right - 8) this.nav.scrollLeft += linkRect.right - navRect.right + 8;
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    if (this.frame !== null) cancelAnimationFrame(this.frame);
  }
}

if (!customElements.get('sterling-bundle-navigation')) customElements.define('sterling-bundle-navigation', SterlingBundleNavigation);

// Reuse the authored groups and native fragment links in one review explorer.
// Individual/mixed layouts retain their ordinary stacked showcases.
class SterlingBundleExplorer extends HTMLElement {
  connectedCallback() {
    this.groups = [...this.querySelectorAll('sterling-bundle-navigation')];
    this.nav = this.querySelector('.sterling-bundle-showcase__groups');
    if (!this.groups.length || this.groups.some((group) => group.dataset.layout !== 'selector')) return;
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.links = this.nav ? [...this.nav.querySelectorAll('a')] : [];
    this.groups.forEach((group) => { group.dataset.explorerReady = 'true'; });
    this.addEventListener('click', (event) => {
      const link = event.target.closest('a');
      if (!this.links.includes(link) || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const group = this.groupForHash(link.hash);
      if (!group) return;
      event.preventDefault();
      if (location.hash !== link.hash) history.pushState({ ...history.state, scrollTop: getScrollContainer().scrollTop }, '', link.hash);
      this.show(group);
      // Moving the single navigation row must preserve the activated link's focus.
      link.focus({ preventScroll: true });
    }, { signal });
    window.addEventListener('hashchange', () => {
      const group = this.groupForHash(location.hash);
      if (group) this.show(group);
    }, { signal });
    this.show(this.groupForHash(location.hash) || this.groups[0]);
  }

  groupForHash(hash) {
    const target = document.getElementById(hash.slice(1));
    return this.groups.find((group) => target && group.contains(target));
  }

  show(selected) {
    this.groups.forEach((group) => {
      const hidden = group !== selected;
      if (hidden) group.querySelectorAll('product-model').forEach((model) => model.pauseMedia?.());
      group.hidden = hidden;
    });
    if (this.nav) selected.insertBefore(this.nav, selected.querySelector('.sterling-bundle-showcase__chooser'));
    this.links.forEach((link) => {
      if (this.groupForHash(link.hash) === selected) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
    selected.schedule?.();
  }

  disconnectedCallback() {
    this.controller?.abort();
    if (!this.controller) return;
    if (this.nav) this.prepend(this.nav);
    this.groups.forEach((group) => { group.hidden = false; delete group.dataset.explorerReady; });
    this.links.forEach((link) => link.removeAttribute('aria-current'));
  }
}

if (!customElements.get('sterling-bundle-explorer')) customElements.define('sterling-bundle-explorer', SterlingBundleExplorer);
